// Patients directory — big deterministic mock (rcm-24 §7, 00-ds-09 §7, D148).
// A practice has 2,000–8,000 patients; this seeds ~2,486 so the directory
// behaves like the server-backed search it will be: a filter, a search over
// name/DOB/phone/member id/chart number, insurance-state/payer/location
// filters, and numbered pages. Real rows come from the PMS sync (§7/§8); this
// only exists so the demo can page, filter and search over a realistic volume.

import { generate, rng, pick, int, fullName, PAYERS, MOCK_NOW } from '@/components/lists';
import { schedulePeople } from '@/features/calendar/readinessMock';

/** Which day the next visit falls on, relative to MOCK_NOW. */
export type VisitBucket = 'today' | 'tomorrow' | 'other' | 'none';

/**
 * The verification state of the patient's primary plan (rcm-24 §3/§4). Drives
 * both the insurance-state filter and the dot + words in the Insurance column.
 *   verified        — green dot
 *   pre_auth        — amber; a pre-auth is outstanding ("Not ready")
 *   no_insurance    — amber; nothing on file ("Not ready")
 *   coverage_ended  — amber; the plan lapsed ("Not ready")
 *   checking        — grey; a check is running right now
 *   not_checked     — grey; a check is scheduled
 */
export type InsState = 'verified' | 'pre_auth' | 'no_insurance' | 'coverage_ended' | 'checking' | 'not_checked';

/** The amber states — the ones the band counts as "aren't ready". */
export const NOT_READY_STATES: InsState[] = ['pre_auth', 'no_insurance', 'coverage_ended'];

export interface PatientRow {
  id: string;
  name: string;
  /** "Last, First" — matched by search and used for the all-patients sort. */
  lastFirst: string;
  lastName: string;
  dobIso: string;
  /** US date "04/02/1988". */
  dobLabel: string;
  phone: string;
  chart: string;
  location: string;

  bucket: VisitBucket;
  /** "Today 8:00 AM", "Mon, Oct 19 8:00 AM", or '' when none booked. */
  nextVisitLabel: string;
  /** "Dr. Carter" / "Hygiene" — shown after the label; '' when none. */
  nextVisitProvider: string;
  /** Visit kind, for the patient page header ("Hygiene + perio eval"). */
  nextVisitType: string;
  /** Full ISO datetime of the next visit, or '' when none. */
  nextVisitAtIso: string;
  /** Sort key: smaller is sooner; none sorts last. */
  visitSort: number;

  insState: InsState;
  /** The words shown in the Insurance column. */
  insLabel: string;

  /** Primary payer, or '' when no insurance is on file. */
  primaryPayer: string;
  /** Member id, or '' when none (no insurance, or a check hasn't returned it). */
  memberId: string;

  /** Remaining yearly maximum in whole dollars, or null when not applicable. */
  yearlyMaxLeft: number | null;
}

const LOCATIONS = ['San Antonio', 'Stone Oak', 'Boerne'];
const PROVIDERS = ['Dr. Carter', 'Dr. Poe', 'Dr. Nguyen', 'Hygiene'];
const VISIT_TYPES = ['Hygiene + perio eval', 'Crown seat', 'New patient exam', 'Composite filling', 'Consult', 'Adult cleaning'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TOTAL = 2486;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** "04/02/1988" */
function usSlash(d: Date): string {
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${String(d.getFullYear())}`;
}

/** "Mon, Oct 19" */
function dayLabel(d: Date): string {
  return `${DOW[d.getDay()] ?? 'Mon'}, ${MONTHS[d.getMonth()] ?? 'Jan'} ${String(d.getDate())}`;
}

function phone(r: () => number): string {
  const area = int(r, 201, 989);
  const mid = int(r, 200, 999);
  const last = int(r, 0, 9999);
  return `(${String(area)}) ${String(mid)}-${String(last).padStart(4, '0')}`;
}

function memberId(r: () => number): string {
  const L = 'ABCDEFGHJKLMNPRTUVWXYZ';
  const a = L[int(r, 0, L.length - 1)] ?? 'A';
  const b = L[int(r, 0, L.length - 1)] ?? 'B';
  const n = String(int(r, 100000, 999999));
  const tail = String(int(r, 10, 99));
  // A spread of real-looking shapes: "AB-884210", "A4471-23", "88142037".
  const shape = int(r, 0, 2);
  if (shape === 0) return `${a}${b}-${n}`;
  if (shape === 1) return `${a}${String(int(r, 1000, 9999))}-${tail}`;
  return `${n}${String(int(r, 10, 99))}`;
}

function timeLabel(h24: number, m: number): string {
  const ap = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h12)}:${pad(m)} ${ap}`;
}

/** The words for an insurance state (rcm-24 §4). */
function labelFor(state: InsState, r: () => number): string {
  switch (state) {
    case 'verified':
      return 'Verified yesterday';
    case 'pre_auth':
      return pick(r, ['Pre-auth: no answer yet', 'Pre-auth needed']);
    case 'no_insurance':
      return 'No insurance on file';
    case 'coverage_ended':
      return `Coverage ended ${pick(r, ['Sep 30', 'Aug 31', 'Jul 31'])}`;
    case 'checking':
      return 'Checking now';
    case 'not_checked':
      return pick(r, ['Checks Sun, Oct 18', 'Checks Sat, Oct 17', 'Checks Mon, Oct 19']);
  }
}

/** A remaining yearly maximum for a state (null when it doesn't apply). */
function yearlyMaxFor(state: InsState, r: () => number): number | null {
  if (state === 'no_insurance' || state === 'coverage_ended') return null;
  return int(r, 0, 200) * 10; // $0 – $2,000 in $10 steps
}

/** The dot colour kind for a state. */
export function dotKind(state: InsState): 'ok' | 'warn' | 'mute' {
  if (state === 'verified') return 'ok';
  if (state === 'checking' || state === 'not_checked') return 'mute';
  return 'warn';
}

// ── Base rows: identity + a weighted insurance state, all not-yet-visiting. ──
const base: PatientRow[] = generate<PatientRow>(TOTAL, 0x7a71, (r, i) => {
  const name = fullName(r);
  const parts = name.split(' ');
  const first = parts[0] ?? name;
  const last = parts.slice(1).join(' ') || first;
  const dob = new Date(1945 + int(r, 0, 70), int(r, 0, 11), int(r, 1, 28));

  // Weighted base insurance state (clearing the filter should show a real mix).
  const x = r();
  let state: InsState;
  if (x < 0.45) state = 'verified';
  else if (x < 0.65) state = 'not_checked';
  else if (x < 0.75) state = 'checking';
  else if (x < 0.83) state = 'pre_auth';
  else if (x < 0.93) state = 'no_insurance';
  else state = 'coverage_ended';

  const payer = state === 'no_insurance' ? '' : pick(r, PAYERS);
  const mid = state === 'no_insurance' || state === 'checking' ? '' : memberId(r);

  return {
    id: `pt-${String(i)}`,
    name,
    lastFirst: `${last}, ${first}`,
    lastName: last,
    dobIso: dob.toISOString().slice(0, 10),
    dobLabel: usSlash(dob),
    phone: phone(r),
    chart: String(int(r, 10000, 29999)),
    location: pick(r, LOCATIONS),

    bucket: 'none',
    nextVisitLabel: '',
    nextVisitProvider: '',
    nextVisitType: '',
    nextVisitAtIso: '',
    visitSort: Number.MAX_SAFE_INTEGER,

    insState: state,
    insLabel: labelFor(state, r),
    primaryPayer: payer,
    memberId: mid,
    yearlyMaxLeft: yearlyMaxFor(state, r),
  };
});

// ── The schedule's people are directory patients. Each person the calendar names
//    (a visit's patient, a family member on a plan) takes over one base row's
//    identity, so a chart number on the calendar opens that person's record.
//    Row count and ids don't change. Deterministic. ──
const CHART_TO_ID = new Map<string, string>();
{
  const taken = new Set<number>();
  for (const person of schedulePeople()) {
    let idx = 0;
    for (const ch of person.chart) idx = (idx * 31 + ch.charCodeAt(0)) % TOTAL;
    while (taken.has(idx)) idx = (idx + 1) % TOTAL;
    taken.add(idx);
    const p = base[idx];
    if (!p) continue;
    const parts = person.name.split(' ');
    const first = parts[0] ?? person.name;
    const last = parts.slice(1).join(' ') || first;
    p.name = person.name;
    p.lastFirst = `${last}, ${first}`;
    p.lastName = last;
    p.chart = person.chart;
    if (person.dob) {
      const [mm, dd, yyyy] = person.dob.split('/');
      if (mm && dd && yyyy) {
        p.dobIso = `${yyyy}-${mm}-${dd}`;
        p.dobLabel = person.dob;
      }
    }
    CHART_TO_ID.set(person.chart, p.id);
  }
}

/** The directory id for a chart number the calendar shows, or undefined when no such person is on the schedule. */
export function patientIdForChart(chart: string): string | undefined {
  return CHART_TO_ID.get(chart);
}

// ── Give ~40% of the non-visiting patients a future (non-today/tomorrow) visit
//    so an all-patients sort and the directory read as a live practice. ──
{
  const r = rng(0x31f5);
  for (const p of base) {
    if (r() < 0.4) {
      const dayOffset = int(r, 3, 120);
      const d = new Date(MOCK_NOW);
      d.setDate(d.getDate() + dayOffset);
      const h = int(r, 8, 16);
      const m = pick(r, [0, 15, 30, 45]);
      d.setHours(h, m, 0, 0);
      const prov = pick(r, PROVIDERS);
      p.bucket = 'other';
      p.nextVisitLabel = `${dayLabel(d)} ${timeLabel(h, m)}`;
      p.nextVisitProvider = prov;
      p.nextVisitType = pick(r, VISIT_TYPES);
      p.nextVisitAtIso = d.toISOString();
      p.visitSort = dayOffset * 1440 + h * 60 + m;
    }
  }
}

// ── The visiting cohort: exactly 41 patients visiting today or tomorrow, of
//    which exactly 6 "aren't ready" (an amber state). Deterministic. ──
const VISITING = 41;
const NOT_READY = 6;
{
  const r = rng(0x5c0d);
  // Pick 41 distinct indices.
  const chosen = new Set<number>();
  while (chosen.size < VISITING) chosen.add(int(r, 0, TOTAL - 1));
  const ids = [...chosen];

  const amber: InsState[] = ['pre_auth', 'no_insurance', 'coverage_ended', 'pre_auth', 'no_insurance', 'coverage_ended'];
  const grey: InsState[] = ['checking', 'not_checked', 'not_checked', 'checking', 'not_checked', 'not_checked', 'checking', 'not_checked'];

  ids.forEach((idx, k) => {
    const p = base[idx];
    if (!p) return;
    // today for the first ~18, tomorrow for the rest.
    const today = k < 18;
    const d = new Date(MOCK_NOW);
    if (!today) d.setDate(d.getDate() + 1);
    const h = int(r, 8, 16);
    const m = pick(r, [0, 15, 30, 45]);
    d.setHours(h, m, 0, 0);
    const prov = pick(r, PROVIDERS);

    p.bucket = today ? 'today' : 'tomorrow';
    p.nextVisitLabel = `${today ? 'Today' : 'Tomorrow'} ${timeLabel(h, m)}`;
    p.nextVisitProvider = prov;
    p.nextVisitType = pick(r, VISIT_TYPES);
    p.nextVisitAtIso = d.toISOString();
    p.visitSort = (today ? 0 : 1) * 1440 + h * 60 + m;

    // State: 6 amber (not ready), 8 grey, the rest verified.
    let state: InsState;
    if (k < NOT_READY) state = amber[k] ?? 'pre_auth';
    else if (k < NOT_READY + grey.length) state = grey[k - NOT_READY] ?? 'not_checked';
    else state = 'verified';
    p.insState = state;
    p.insLabel = labelFor(state, r);
    p.primaryPayer = state === 'no_insurance' ? '' : p.primaryPayer || pick(r, PAYERS);
    p.memberId = state === 'no_insurance' || state === 'checking' ? '' : p.memberId || memberId(r);
    p.yearlyMaxLeft = yearlyMaxFor(state, r);
  });
}

/** Every patient, sorted by last name (the default all-patients order). */
export const PATIENTS: PatientRow[] = [...base].sort((a, b) => a.lastFirst.localeCompare(b.lastFirst));

/** The distinct locations present in the mock (for the location filter). */
export const PATIENT_LOCATIONS: string[] = [...new Set(PATIENTS.map((p) => p.location))].sort((a, b) => a.localeCompare(b));

/** Count of patients visiting today or tomorrow (the default view). */
export const VISITING_COUNT = PATIENTS.filter((p) => p.bucket === 'today' || p.bucket === 'tomorrow').length;

/** Of the visiting patients, how many are in an amber ("not ready") state. */
export const NOT_READY_COUNT = PATIENTS.filter(
  (p) => (p.bucket === 'today' || p.bucket === 'tomorrow') && NOT_READY_STATES.includes(p.insState),
).length;

/** Look up one patient by id (used by the patient page, so it renders THAT patient). */
export function getPatient(id: string | undefined): PatientRow | undefined {
  if (!id) return undefined;
  return PATIENTS.find((p) => p.id === id);
}
